import React, { useMemo, useState, useCallback } from "react";
import moment from "moment";
import { Tabs, Empty, Button, message } from "antd";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { getReportUpdateSchema } from "../../schema/report.schema";
import { CardElement } from "../../components/Common";
import ViewReportsTopBar from "../../components/Report/ViewReportsTopBar";
import {
  useGetProjectHotelReports,
  useGetProjectSmartScan,
  useGetProjectReportCategories,
  useReportUpdateReport,
  useReportDeleteReport,
  useGetHotelSubHeaderData,
  useGetLMDDocumentTypes,
  useGetHotelById,
} from "../../hooks";

import ViewReportsTable, { getTableColumns } from "../../components/Report/ViewReportsTable";
import {
  REPORT_TYPES,
  convertReportFieldsToText,
  preProcessReportSubmitData,
  getAvailableDocTypes,
} from "../../utils/report";
import ExportCSV from "../../components/Common/ExportCSV";
import ReplicateReports from "../../components/Report/ReplicateReports";

const ViewReports = () => {
  const [hmg, setHmg] = useState();
  const [project, setProject] = useState();
  const [pms, setPMS] = useState();
  const [hotel, setHotel] = useState();
  const [frequency, setFrequency] = useState([]);
  const [reportModalOpen, setReportModalOPen] = useState(false);

  const setHmgRef = useCallback((list) => {
    if (list?.length === 1) setHmg(list[0]._id);
  }, []);
  const setProjectRef = useCallback((list) => {
    if (list?.length === 1) setProject(list[0]._id);
  }, []);
  const setPMSRef = useCallback((list) => {
    if (list?.length === 1) setPMS(list[0]._id);
  }, []);
  const setHotelListRef = useCallback((list) => {
    if (list?.length === 1) setHotel(list[0]._id);
  }, []);

  // const { data: selectedProjectData } = useGetProject(project, {
  //   enabled: !!project,
  // });
  const { data: documentTypes } = useGetLMDDocumentTypes();
  const { data: hotelData } = useGetHotelById(hotel, { enabled: !!hotel });
  const { data: projectSmartScanData } = useGetProjectSmartScan(project, {
    enabled: !!project,
  });
  const { data: hotelSubHeaderData } = useGetHotelSubHeaderData(
    hotel,
    project,
    {
      enabled: !!(hotel && project),
    },
  );

  const { data: projectReportCategories } = useGetProjectReportCategories(
    project,
    {
      enabled: !!project,
    },
  );

  const {
    data: hotelReports,
    refetch: retrieveHotelReports,
    isLoading: isLoadingHotelReports,
  } = useGetProjectHotelReports(project, hotel, {
    enabled: false,
  });

  const availableDocTypes = useMemo(
    () =>
      getAvailableDocTypes({
        hotelData: hotelData ? [hotelData] : [],
        selectedHotels: [hotel],
        documentTypes: documentTypes ?? [],
      }),
    [hotelData, documentTypes],
  );

  const reportUpdateSchema = useMemo(() => {
    return getReportUpdateSchema(hotelReports ?? []);
  }, [hotelReports]);

  const {
    control,
    handleSubmit,
    reset,
    resetField,
    watch,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(reportUpdateSchema),
  });

  const setRecord = (id) => {
    const hotelReport = hotelReports.find((report) => report._id === id);

    reset({
      report: hotelReport
        ? {
            project_id: hotelReport.project_id,
            _id: hotelReport._id,
            ReportName: hotelReport.ReportName,
            PMSReportName: hotelReport.PMSReportName,
            shedule_type: hotelReport.shedule_type,
            report_category: hotelReport.report_category,
            signature_type: hotelReport.signature_type,
            ReportOrder: hotelReport.ReportOrder,
            SignatureMandatory: hotelReport.SignatureMandatory,
            smart_scan_type: hotelReport.smart_scan_type,
            enable_redaction: hotelReport.enable_redaction,
            export_flat_files: hotelReport.export_flat_files,
            split_by_sub_header: hotelReport.split_by_sub_header,
            document_type: hotelReport.document_type,
            SourceType: hotelReport.SourceType,
            schedule_trigger_type: hotelReport.schedule_trigger_type,
            schedule_weekday: hotelReport.schedule_weekday,
            schedule_trigger_data: hotelReport.schedule_trigger_data,
            schedule_months: hotelReport.schedule_months,
            business_date: hotelReport.business_date,
            emailSubjects: hotelReport.emailSubjects,
            filenames: hotelReport.filenames,
            FileExtension: hotelReport.FileExtension,
            FileMappingName: hotelReport.FileMappingName,
            QC_IgnoredReport: hotelReport.QC_IgnoredReport,
            ComplianceEnabled: hotelReport.ComplianceEnabled,
            report_name_matching_condition:
              hotelReport.report_name_matching_condition,
            file_extension_mapping: hotelReport.file_extension_mapping,
            activation_date: hotelReport.activation_date
              ? moment(hotelReport.activation_date)
              : undefined,
            deactivation_date: hotelReport.deactivation_date
              ? moment(hotelReport.deactivation_date)
              : undefined,
            signature_activation_date: hotelReport.signature_activation_date
              ? moment(hotelReport.signature_activation_date)
              : undefined,
            signature_deactivation_date: hotelReport.signature_deactivation_date
              ? moment(hotelReport.signature_deactivation_date)
              : undefined,
            report_type: hotelReport.report_type,
            mydocs_report_type: hotelReport.mydocs_report_type,
            create_by: hotelReport.create_by,
            create_at: hotelReport.create_at,
            update_by: hotelReport.update_by,
            update_at: hotelReport.update_at,
          }
        : undefined,
    });
  };

  const { mutate: updateReport, isLoading: isReportUpdating } =
    useReportUpdateReport({
      onError: (error) => {
        message.error(error.response?.data?.message ?? "Server Error");
      },
      onSuccess: () => {
        message.success("Report updated successfully");
      },
      onSettled: () => {
        setRecord();
        retrieveHotelReports();
      },
    });

  const { mutate: deleteReport, isLoading: isReportDeleting } =
    useReportDeleteReport({
      onError: (error) => {
        message.error(error.response?.data?.message ?? "Server Error");
      },
      onSuccess: () => {
        message.success("Report Deleted");
      },
      onSettled: () => {
        setRecord(null);
        retrieveHotelReports();
      },
    });

  const selectedRecordId = watch("report._id");

  const tableDataSource = useMemo(() => {
    const hotelreports = hotelReports;

    return projectSmartScanData &&
      projectReportCategories &&
      Array.isArray(hotelreports)
      ? hotelreports.map((report) => {
          return convertReportFieldsToText(report, {
            projectReportCategories,
            projectSmartScanData,
            documentTypes: availableDocTypes,
            hotelSubHeaderData,
          });
        })
      : [];
  }, [hotelReports]);

  const onError = () => {
    message.error("Please fix all the input errors");
  };

  const submitFn = (data) => {
    const sendData = {
      ...data.report,
      hotel_id: hotel,
      ...preProcessReportSubmitData(data.report),
    };

    updateReport({ id: sendData._id, data: sendData });
  };

  const reportOperations = useMemo(() => {
    return (
      <>
        <Link to={`/report-management/copy-over/${project}/${pms}/${hotel}`}>
          <Button type="link" disabled={pms === "all"}>
            Copy Over PMS Reports
          </Button>
        </Link>
        <Button
          type="link"
          disabled={!hotelReports?.length}
          onClick={() => setReportModalOPen(true)}
        >
          Replicate Reports
        </Button>
      </>
    );
  }, [project, pms, hotel, hotelReports]);

  const reportFrequencyOnChange = (selectFrequency) => {
    setFrequency(selectFrequency);
  };

  return (
    <CardElement title="Advanced Report Management">
      <ViewReportsTopBar
        hmg={hmg}
        setHmg={setHmg}
        setHmgRef={setHmgRef}
        project={project}
        setProject={setProject}
        setProjectRef={setProjectRef}
        pms={pms}
        setPMS={setPMS}
        setPMSRef={setPMSRef}
        hotel={hotel}
        setHotel={setHotel}
        setHotelListRef={setHotelListRef}
        frequency={frequency}
        setFrequency={reportFrequencyOnChange}
        dataRetrievalFn={retrieveHotelReports}
        isLoading={isLoadingHotelReports}
      />

      <form onSubmit={handleSubmit(submitFn, onError)}>
        {frequency.length > 0 && hotelReports ? (
          <Tabs
            defaultActiveKey="1"
            tabBarExtraContent={reportOperations}
            items={REPORT_TYPES.filter((type) =>
              frequency.includes(type.ReportType),
            ).map((elm) => {
              const reportType = elm.ReportType;
              return {
                label: reportType,
                key: reportType,
                children:
                  tableDataSource.filter(
                    (report) => report.shedule_type === reportType,
                  ).length === 0 ? (
                    <Empty
                      description={
                        <p>
                          No data is available for the selected filters. <br />
                          Please click <b>Copy over PMS Reports</b> to add
                          Report.
                        </p>
                      }
                    />
                  ) : (
                    <>
                      <ExportCSV
                        csvData={tableDataSource.filter(
                          (report) => report.shedule_type === reportType,
                        )}
                        fileName={`${reportType} Reports`}
                        columnNames={getTableColumns({
                          setRecord: () => {},
                          type: reportType,
                        }).filter((col) => col.dataIndex !== "operation")}
                      />
                      <ViewReportsTable
                        reportCategories={projectReportCategories}
                        dataSource={tableDataSource.filter(
                          (report) => report.shedule_type === reportType,
                        )}
                        type={reportType}
                        setRecord={setRecord}
                        selectedRecordId={selectedRecordId}
                        userAccess={{
                          delete: true,
                        }}
                        formParams={{
                          control,
                          errors,
                          watch,
                          setValue,
                          getValues,
                          resetField,
                        }}
                        isUpdating={isReportUpdating}
                        isDeleting={isReportDeleting}
                        updateFn={handleSubmit}
                        deleteReportFn={(id) =>
                          deleteReport({ id, hotel_id: hotel })
                        }
                        documentTypes={availableDocTypes}
                      />
                    </>
                  ),
              };
            })}
          />
        ) : (
          <Empty
            description={(
              <span>
                Please select your <b>Filters</b> and click the <b>Go Button</b>
              </span>
            )}
          />
        )}
      </form>
      <ReplicateReports
        reportModalOpen={reportModalOpen}
        setReportModalOPen={setReportModalOPen}
        hotel={hotel}
        pmsId={pms}
        projectId={project}
      />
    </CardElement>
  );
};

ViewReports.propTypes = {};

export default ViewReports;
